// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { PageSettings, PagedAsyncIterableIterator } from "@azure/core-paging";
import type { ContinuablePage } from "./static-helpers/pagingHelpers.js";
export { RestError } from "@azure/core-rest-pipeline";
export { AIProjectClient } from "./aiProjectClient.js";
export type {
  Agent,
  AgentVersion,
  AgentDefinition,
  AgentDefinitionUnion,
  AgentKind,
  RaiConfig,
  HostedAgentDefinition,
  Tool,
  ToolUnion,
  ToolType,
  BingGroundingTool,
  BingGroundingSearchToolParameters,
  BingGroundingSearchConfiguration,
  MicrosoftFabricPreviewTool,
  FabricDataAgentToolParameters,
  ToolProjectConnection,
  SharepointPreviewTool,
  SharepointGroundingToolParameters,
  AzureAISearchTool,
  AzureAISearchToolResource,
  AISearchIndexResource,
  AzureAISearchQueryType,
  OpenApiTool,
  OpenApiFunctionDefinition,
  OpenApiAuthDetails,
  OpenApiAuthDetailsUnion,
  OpenApiAuthType,
  OpenApiAnonymousAuthDetails,
  OpenApiProjectConnectionAuthDetails,
  OpenApiProjectConnectionSecurityScheme,
  OpenApiManagedAuthDetails,
  OpenApiManagedSecurityScheme,
  BingCustomSearchPreviewTool,
  BingCustomSearchToolParameters,
  BingCustomSearchConfiguration,
  BrowserAutomationPreviewTool,
  BrowserAutomationToolParameters,
  BrowserAutomationToolConnectionParameters,
  AzureFunctionTool,
  AzureFunctionDefinition,
  AzureFunctionBinding,
  AzureFunctionStorageQueue,
  CaptureStructuredOutputsTool,
  StructuredOutputDefinition,
  A2APreviewTool,
  WorkIQPreviewTool,
  WorkIQPreviewToolParameters,
  MemorySearchPreviewTool,
  MemorySearchOptions,
  CodeInterpreterTool,
  AutoCodeInterpreterToolParam,
  ContainerMemoryLimit,
  ContainerNetworkPolicyParam,
  ContainerNetworkPolicyParamUnion,
  ContainerNetworkPolicyParamType,
  ContainerNetworkPolicyDisabledParam,
  ContainerNetworkPolicyAllowlistParam,
  ContainerNetworkPolicyDomainSecretParam,
  FunctionTool,
  FileSearchTool,
  RankingOptions,
  RankerVersionType,
  HybridSearchOptions,
  Filters,
  ComparisonFilter,
  ComparisonFilterValueItems,
  CompoundFilter,
  ComputerUsePreviewTool,
  ComputerEnvironment,
  WebSearchTool,
  WebSearchToolFilters,
  WebSearchApproximateLocation,
  WebSearchConfiguration,
  MCPTool,
  MCPToolFilter,
  MCPToolRequireApproval,
  ImageGenTool,
  InputFidelity,
  ImageGenToolInputImageMask,
  ImageGenAction,
  LocalShellToolParam,
  FunctionShellToolParam,
  FunctionShellToolParamEnvironment,
  FunctionShellToolParamEnvironmentUnion,
  FunctionShellToolParamEnvironmentType,
  FunctionShellToolParamEnvironmentLocalEnvironmentParam,
  LocalSkillParam,
  FunctionShellToolParamEnvironmentContainerReferenceParam,
  ContainerAutoParam,
  ContainerSkill,
  ContainerSkillUnion,
  ContainerSkillType,
  SkillReferenceParam,
  InlineSkillParam,
  InlineSkillSourceParam,
  CustomToolParam,
  CustomToolParamFormat,
  CustomToolParamFormatUnion,
  CustomToolParamFormatType,
  CustomTextFormatParam,
  CustomGrammarFormatParam,
  GrammarSyntax,
  WebSearchPreviewTool,
  ApproximateLocation,
  SearchContextSize,
  SearchContentType,
  ApplyPatchToolParam,
  ComputerTool,
  NamespaceToolParam,
  FunctionToolParam,
  EmptyModelParam,
  ToolSearchToolParam,
  ToolSearchExecutionType,
  ProtocolVersionRecord,
  AgentProtocol,
  ContainerConfiguration,
  CodeConfiguration,
  PromptAgentDefinition,
  Reasoning,
  ReasoningEffort,
  ToolChoiceParam,
  ToolChoiceParamUnion,
  ToolChoiceParamType,
  ToolChoiceAllowed,
  ToolChoiceFunction,
  ToolChoiceMCP,
  ToolChoiceCustom,
  SpecificApplyPatchParam,
  SpecificFunctionShellParam,
  ToolChoiceFileSearch,
  ToolChoiceWebSearchPreview,
  ToolChoiceComputerUsePreview,
  ToolChoiceWebSearchPreview20250311,
  ToolChoiceImageGeneration,
  ToolChoiceCodeInterpreter,
  ToolChoiceComputer,
  ToolChoiceComputerUse,
  PromptAgentDefinitionTextOptions,
  TextResponseFormat,
  TextResponseFormatUnion,
  TextResponseFormatConfigurationType,
  TextResponseFormatJsonSchema,
  TextResponseFormatText,
  TextResponseFormatJsonObject,
  StructuredInputDefinition,
  WorkflowAgentDefinition,
  AgentIdentity,
  AgentBlueprintReference,
  AgentBlueprintReferenceUnion,
  AgentBlueprintReferenceType,
  ManagedAgentIdentityBlueprintReference,
  AgentEndpoint,
  VersionSelector,
  VersionSelectionRule,
  VersionSelectionRuleUnion,
  VersionSelectorType,
  FixedRatioVersionSelectionRule,
  AgentEndpointProtocol,
  AgentEndpointAuthorizationScheme,
  AgentEndpointAuthorizationSchemeUnion,
  AgentEndpointAuthorizationSchemeType,
  EntraAuthorizationScheme,
  IsolationKeySource,
  IsolationKeySourceUnion,
  IsolationKeySourceKind,
  EntraIsolationKeySource,
  HeaderIsolationKeySource,
  BotServiceAuthorizationScheme,
  BotServiceRbacAuthorizationScheme,
  AgentCard,
  AgentCardSkill,
  ApiErrorResponse,
  ErrorModel,
  DeleteAgentResponse,
  DeleteAgentVersionResponse,
  EvaluationRule,
  EvaluationRuleAction,
  EvaluationRuleActionUnion,
  EvaluationRuleActionType,
  ContinuousEvaluationRuleAction,
  HumanEvaluationPreviewRuleAction,
  EvaluationRuleFilter,
  EvaluationRuleEventType,
  Connection,
  ConnectionType,
  BaseCredentials,
  BaseCredentialsUnion,
  CredentialType,
  ApiKeyCredentials,
  EntraIDCredentials,
  CustomCredential,
  SASTokenCredentials,
  NoAuthenticationCredentials,
  AgenticIdentityPreviewCredentials,
  DatasetVersion,
  DatasetVersionUnion,
  DatasetType,
  FileDatasetVersion,
  FolderDatasetVersion,
  PendingUploadRequest,
  PendingUploadResponse,
  BlobReference,
  SasCredential,
  DatasetCredential,
  Deployment,
  DeploymentUnion,
  DeploymentType,
  ModelDeployment,
  ModelDeploymentSku,
  Index,
  IndexUnion,
  IndexType,
  AzureAISearchIndex,
  FieldMapping,
  ManagedAzureAISearchIndex,
  CosmosDBIndex,
  EmbeddingConfiguration,
  VersionIndicator,
  VersionIndicatorUnion,
  VersionIndicatorType,
  VersionRefIndicator,
  AgentSessionResource,
  AgentSessionStatus,
  SessionFileWriteResponse,
  SessionDirectoryListResponse,
  SessionDirectoryEntry,
  EvaluationTaxonomy,
  EvaluationTaxonomyInput,
  EvaluationTaxonomyInputUnion,
  EvaluationTaxonomyInputType,
  AgentTaxonomyInput,
  Target,
  TargetUnion,
  AzureAIModelTarget,
  ModelSamplingParams,
  AzureAIAgentTarget,
  ToolDescription,
  RiskCategory,
  TaxonomyCategory,
  TaxonomySubCategory,
  EvaluatorVersion,
  EvaluatorType,
  EvaluatorCategory,
  EvaluatorDefinition,
  EvaluatorDefinitionUnion,
  EvaluatorDefinitionType,
  EvaluatorMetric,
  EvaluatorMetricType,
  EvaluatorMetricDirection,
  CodeBasedEvaluatorDefinition,
  PromptBasedEvaluatorDefinition,
  Insight,
  InsightsMetadata,
  OperationState,
  InsightRequest,
  InsightRequestUnion,
  InsightType,
  EvaluationRunClusterInsightRequest,
  InsightModelConfiguration,
  AgentClusterInsightRequest,
  EvaluationComparisonInsightRequest,
  InsightResult,
  InsightResultUnion,
  EvaluationComparisonInsightResult,
  EvalRunResultComparison,
  EvalRunResultSummary,
  EvalRunResultCompareItem,
  TreatmentEffectType,
  EvaluationRunClusterInsightResult,
  ClusterInsightResult,
  InsightSummary,
  ClusterTokenUsage,
  InsightCluster,
  InsightSample,
  InsightSampleUnion,
  SampleType,
  EvaluationResultSample,
  EvalResult,
  ChartCoordinate,
  AgentClusterInsightResult,
  MemoryStoreDefinition,
  MemoryStoreDefinitionUnion,
  MemoryStoreKind,
  MemoryStoreDefaultDefinition,
  MemoryStoreDefaultOptions,
  MemoryStore,
  DeleteMemoryStoreResponse,
  MemoryStoreSearchResponse,
  MemorySearchItem,
  MemoryItem,
  MemoryItemUnion,
  MemoryItemKind,
  UserProfileMemoryItem,
  ChatSummaryMemoryItem,
  MemoryStoreOperationUsage,
  ResponseUsageInputTokensDetails,
  ResponseUsageOutputTokensDetails,
  MemoryStoreUpdateResponse,
  MemoryStoreUpdateStatus,
  MemoryStoreUpdateCompletedResult,
  MemoryOperation,
  MemoryOperationKind,
  MemoryStoreDeleteScopeResponse,
  RedTeam,
  AttackStrategy,
  TargetConfig,
  TargetConfigUnion,
  AzureOpenAIModelConfiguration,
  Schedule,
  ScheduleProvisioningStatus,
  Trigger,
  TriggerUnion,
  TriggerType,
  CronTrigger,
  RecurrenceTrigger,
  RecurrenceSchedule,
  RecurrenceScheduleUnion,
  RecurrenceType,
  HourlyRecurrenceSchedule,
  DailyRecurrenceSchedule,
  WeeklyRecurrenceSchedule,
  DayOfWeek,
  MonthlyRecurrenceSchedule,
  OneTimeTrigger,
  ScheduleTask,
  ScheduleTaskUnion,
  ScheduleTaskType,
  EvaluationScheduleTask,
  InsightScheduleTask,
  ScheduleRun,
  ToolboxPolicies,
  ToolboxVersionObject,
  ToolboxObject,
  SkillObject,
  DeleteSkillResponse,
  UpdateToolboxRequest,
  AgentType,
  AgentDefinitionOptInKeys,
  PageOrder,
  FoundryFeaturesOptInKeys,
  PendingUploadType,
  MemoryStoreType,
  KnownApiVersions,
  BetaSkillsDownloadResponse,
  BetaAgentsDownloadSessionFileResponse,
} from "./models/index.js";
export type { AIProjectClientOptionalParams, DatasetUploadOptions } from "./api/index.js";
export type {
  AgentsListVersionsOptionalParams,
  AgentsDeleteVersionOptionalParams,
  AgentsGetVersionOptionalParams,
  AgentsCreateAgentVersionFromManifestOptionalParams,
  AgentsCreateVersionOptionalParams,
  AgentsListOptionalParams,
  AgentsDeleteOptionalParams,
  AgentsUpdateAgentFromManifestOptionalParams,
  AgentsCreateAgentFromManifestOptionalParams,
  AgentsUpdateOptionalParams,
  AgentsCreateOptionalParams,
  AgentsGetOptionalParams,
} from "./api/agents/index.js";
export type {
  ConnectionsListOptionalParams,
  ConnectionsGetWithCredentialsOptionalParams,
  ConnectionsGetOptionalParams,
  ConnectionsGetDefaultOptionalParams,
} from "./api/connections/index.js";
export type {
  DatasetsGetCredentialsOptionalParams,
  DatasetsPendingUploadOptionalParams,
  DatasetsCreateOrUpdateOptionalParams,
  DatasetsDeleteOptionalParams,
  DatasetsGetOptionalParams,
  DatasetsListOptionalParams,
  DatasetsListVersionsOptionalParams,
} from "./api/datasets/index.js";
export type {
  DeploymentsListOptionalParams,
  DeploymentsGetOptionalParams,
} from "./api/deployments/index.js";
export type {
  EvaluationRulesListOptionalParams,
  EvaluationRulesCreateOrUpdateOptionalParams,
  EvaluationRulesDeleteOptionalParams,
  EvaluationRulesGetOptionalParams,
} from "./api/evaluationRules/index.js";
export type {
  IndexesCreateOrUpdateOptionalParams,
  IndexesDeleteOptionalParams,
  IndexesGetOptionalParams,
  IndexesListOptionalParams,
  IndexesListVersionsOptionalParams,
} from "./api/indexes/index.js";
export type {
  BetaAgentsDeleteSessionFileOptionalParams,
  BetaAgentsListSessionFilesOptionalParams,
  BetaAgentsDownloadSessionFileOptionalParams,
  BetaAgentsUploadSessionFileOptionalParams,
  BetaAgentsListSessionsOptionalParams,
  BetaAgentsDeleteSessionOptionalParams,
  BetaAgentsGetSessionOptionalParams,
  BetaAgentsCreateSessionOptionalParams,
  BetaAgentsPatchAgentObjectOptionalParams,
} from "./api/beta/agents/index.js";
export type {
  BetaEvaluationTaxonomiesUpdateOptionalParams,
  BetaEvaluationTaxonomiesCreateOptionalParams,
  BetaEvaluationTaxonomiesDeleteOptionalParams,
  BetaEvaluationTaxonomiesListOptionalParams,
  BetaEvaluationTaxonomiesGetOptionalParams,
} from "./api/beta/evaluationTaxonomies/index.js";
export type {
  BetaEvaluatorsUpdateVersionOptionalParams,
  BetaEvaluatorsCreateVersionOptionalParams,
  BetaEvaluatorsDeleteVersionOptionalParams,
  BetaEvaluatorsGetVersionOptionalParams,
  BetaEvaluatorsListOptionalParams,
  BetaEvaluatorsListVersionsOptionalParams,
} from "./api/beta/evaluators/index.js";
export type {
  BetaInsightsListOptionalParams,
  BetaInsightsGetOptionalParams,
  BetaInsightsGenerateOptionalParams,
} from "./api/beta/insights/index.js";
export type {
  BetaMemoryStoresDeleteScopeOptionalParams,
  BetaMemoryStoresGetUpdateResultOptionalParams,
  BetaMemoryStoresUpdateMemoriesOptionalParams,
  BetaMemoryStoresSearchMemoriesOptionalParams,
  BetaMemoryStoresDeleteOptionalParams,
  BetaMemoryStoresListOptionalParams,
  BetaMemoryStoresGetOptionalParams,
  BetaMemoryStoresUpdateOptionalParams,
  BetaMemoryStoresCreateOptionalParams,
} from "./api/beta/memoryStores/index.js";
export type {
  BetaRedTeamsCreateOptionalParams,
  BetaRedTeamsListOptionalParams,
  BetaRedTeamsGetOptionalParams,
} from "./api/beta/redTeams/index.js";
export type {
  BetaSchedulesListRunsOptionalParams,
  BetaSchedulesGetRunOptionalParams,
  BetaSchedulesCreateOrUpdateOptionalParams,
  BetaSchedulesListOptionalParams,
  BetaSchedulesGetOptionalParams,
  BetaSchedulesDeleteOptionalParams,
} from "./api/beta/schedules/index.js";
export type {
  BetaSkillsDeleteOptionalParams,
  BetaSkillsUpdateOptionalParams,
  BetaSkillsListOptionalParams,
  BetaSkillsDownloadOptionalParams,
  BetaSkillsGetOptionalParams,
  CreateFromPackageOptionalParams,
  BetaSkillsCreateOptionalParams,
} from "./api/beta/skills/index.js";
export type {
  BetaToolboxesDeleteVersionOptionalParams,
  BetaToolboxesDeleteOptionalParams,
  BetaToolboxesUpdateOptionalParams,
  BetaToolboxesGetVersionOptionalParams,
  BetaToolboxesListVersionsOptionalParams,
  BetaToolboxesListOptionalParams,
  BetaToolboxesGetOptionalParams,
  BetaToolboxesCreateVersionOptionalParams,
} from "./api/beta/toolboxes/index.js";
export type {
  AgentsOperations,
  BetaOperations,
  ConnectionsOperations,
  DatasetsOperations,
  DeploymentsOperations,
  EvaluationRulesOperations,
  IndexesOperations,
  BetaAgentsOperations,
  TelemetryOperations,
  BetaEvaluationTaxonomiesOperations,
  BetaEvaluatorsOperations,
  BetaInsightsOperations,
  BetaMemoryStoresOperations,
  BetaRedTeamsOperations,
  BetaSchedulesOperations,
  BetaSkillsOperations,
  BetaToolboxesOperations,
} from "./classic/index.js";
export type { PageSettings, ContinuablePage, PagedAsyncIterableIterator };

export type {
  AzureAgentConfig,
  OpenAIClientOptionsWithAzureAgent,
} from "./azureAgent.interface.js";
export type {
  TextResponseFormatConfiguration,
  TextResponseFormatConfigurationResponseFormatText,
  TextResponseFormatConfigurationResponseFormatJsonObject,
  TextResponseFormatConfigurationUnion,
} from "./deprecate.interface.js";
